/**
 * Settings Page
 * School settings (admin only) - Configure school branding for credential documents
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';

interface SchoolSettings {
  schoolName: string;
  schoolLogo: string;
  schoolAddress: string;
  schoolPhone: string;
  schoolEmail: string;
  useCustomHeader: boolean; // false = plain header for letterhead, true = custom header
}

export const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [settings, setSettings] = useState<SchoolSettings>({
    schoolName: '',
    schoolLogo: '',
    schoolAddress: '',
    schoolPhone: '',
    schoolEmail: '',
    useCustomHeader: false, // Default: plain header for school letterhead
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  const loadSettings = () => {
    // Load from localStorage for now
    const saved = localStorage.getItem('schoolSettings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to parse settings:', error);
      }
    } else {
      // Set defaults from user data
      setSettings(prev => ({
        ...prev,
        schoolName: user?.schoolName || '',
      }));
    }
    setIsLoading(false);
  };

  const handleChange = (field: keyof SchoolSettings, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showNotification('error', 'Logo file must be less than 2MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification('error', 'Please upload an image file');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setSettings(prev => ({ ...prev, schoolLogo: base64 }));
        showNotification('success', 'Logo uploaded successfully');
      };
      reader.onerror = () => {
        showNotification('error', 'Failed to upload logo');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({ ...prev, schoolLogo: '' }));
    showNotification('success', 'Logo removed');
  };

  const handleSave = () => {
    setIsSaving(true);
    try {
      // Save to localStorage for now
      localStorage.setItem('schoolSettings', JSON.stringify(settings));
      showNotification('success', 'Settings saved successfully');
    } catch (error) {
      showNotification('error', 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">School Settings</h1>
        <p className="text-slate-600 mt-2">Configure school branding for credential documents</p>
      </div>

      {/* Print Format Settings */}
      <Card title="Print Format Settings">
        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-slate-700 mb-3">
              Credential Document Format
            </p>
            <p className="text-sm text-slate-600 mb-4">
              Choose how student credentials should be formatted when printed by teachers.
            </p>
            
            {/* Toggle for print format */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.useCustomHeader}
                  onChange={(e) => setSettings(prev => ({ ...prev, useCustomHeader: e.target.checked }))}
                  className="mt-1 w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-900">
                    Include school branding on credentials
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {settings.useCustomHeader ? (
                      <>
                        <strong>Enabled:</strong> School logo and contact details will be printed at the top of each credential sheet. 
                        Use this when printing on plain paper.
                      </>
                    ) : (
                      <>
                        <strong>Disabled (Recommended):</strong> Suitable for use with paper with pre-printed school letterheads.
                      </>
                    )}
                  </div>
                </div>
              </label>
            </div>

            {/* Info box */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-900">
                  <strong>Note:</strong> The QR code for downloading the SpellTales app is always included at the bottom of credentials, 
                  regardless of this setting. Each student's credentials will print on a separate page.
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* School Branding */}
      <Card title="School Branding Details">
        <div className="space-y-6">
          <p className="text-sm text-slate-600">
            {settings.useCustomHeader 
              ? 'These details will appear on student credential documents when custom header is enabled.'
              : 'Configure your school details. These will only appear on credentials if you enable custom header above.'}
          </p>

          {/* School Logo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              School Logo
            </label>
            {settings.schoolLogo ? (
              <div className="space-y-3">
                <div className="border-2 border-slate-200 rounded-xl p-4 bg-white inline-block">
                  <img 
                    src={settings.schoolLogo} 
                    alt="School Logo" 
                    className="max-w-[200px] max-h-[100px] object-contain"
                  />
                </div>
                <div>
                  <Button
                    variant="outline"
                    onClick={handleRemoveLogo}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Remove Logo
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                    <svg className="w-12 h-12 mx-auto text-slate-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-slate-600">
                      Click to upload school logo
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      PNG, JPG up to 2MB
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* School Name */}
          <Input
            label="School Name"
            type="text"
            value={settings.schoolName}
            onChange={(e) => handleChange('schoolName', e.target.value)}
            placeholder="Springfield Primary School"
          />

          {/* School Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              School Address
            </label>
            <textarea
              value={settings.schoolAddress}
              onChange={(e) => handleChange('schoolAddress', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="123 Main Street, Springfield, SP1 2AB"
            />
          </div>

          {/* School Phone */}
          <Input
            label="School Phone"
            type="tel"
            value={settings.schoolPhone}
            onChange={(e) => handleChange('schoolPhone', e.target.value)}
            placeholder="01234 567890"
          />

          {/* School Email */}
          <Input
            label="School Email"
            type="email"
            value={settings.schoolEmail}
            onChange={(e) => handleChange('schoolEmail', e.target.value)}
            placeholder="office@school.com"
          />

          {/* Save Button */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>

      {/* Preview */}
      <Card title="Credential Document Preview">
        <div className="text-sm text-slate-600 mb-4">
          {settings.useCustomHeader 
            ? 'Preview of how credentials will look with custom header:'
            : 'Preview of how credentials will look with plain header (for letterhead):'}
        </div>
        
        {settings.useCustomHeader ? (
          /* Custom Header Preview */
          <div className="border-2 border-slate-200 rounded-xl p-6 bg-white">
            <div className="text-center border-b-2 border-slate-900 pb-4">
              {settings.schoolLogo && (
                <img 
                  src={settings.schoolLogo} 
                  alt="School Logo" 
                  className="max-w-[200px] max-h-[100px] object-contain mx-auto mb-2"
                />
              )}
              <div className="text-xl font-bold text-slate-900">
                {settings.schoolName || 'Your School Name'}
              </div>
              {(settings.schoolAddress || settings.schoolPhone || settings.schoolEmail) && (
                <div className="text-xs text-slate-600 mt-2">
                  {settings.schoolAddress && <div>{settings.schoolAddress}</div>}
                  {(settings.schoolPhone || settings.schoolEmail) && (
                    <div>
                      {settings.schoolPhone && `Tel: ${settings.schoolPhone}`}
                      {settings.schoolPhone && settings.schoolEmail && ' | '}
                      {settings.schoolEmail && `Email: ${settings.schoolEmail}`}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 text-center">
              <div className="text-lg font-bold mb-4">SpellTales Login Credentials</div>
              <div className="text-sm text-slate-600 mb-4">Student credentials and instructions appear here...</div>
              <div className="inline-block bg-slate-100 p-4 rounded">
                <div className="w-24 h-24 bg-slate-300 mx-auto mb-2"></div>
                <div className="text-xs text-slate-600">QR Code (Always Included)</div>
              </div>
            </div>
          </div>
        ) : (
          /* Plain Header Preview */
          <div className="border-2 border-slate-200 rounded-xl bg-white">
            <div className="bg-slate-50 border-b-2 border-dashed border-slate-300 p-8 text-center">
              <div className="text-sm text-slate-500 italic">
                Space for Pre-Printed School Letterhead
              </div>
            </div>
            <div className="p-6">
              <div className="text-center">
                <div className="text-lg font-bold mb-4">SpellTales Login Credentials</div>
                <div className="text-sm text-slate-600 mb-4">Student credentials and instructions appear here...</div>
                <div className="inline-block bg-slate-100 p-4 rounded">
                  <div className="w-24 h-24 bg-slate-300 mx-auto mb-2"></div>
                  <div className="text-xs text-slate-600">QR Code (Always Included)</div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-4 text-xs text-slate-500 text-center">
          Preview is simplified. Actual credentials include full student details, username, password, and instructions.
        </div>
      </Card>
    </div>
  );
};

